import { Box, BoxProps, Column, Row } from "@hightouch/htui";
import { FC, PropsWithChildren, ReactNode } from "react";

import { Helmet } from "react-helmet";
import { Paper } from "src/components/paper";
import { Breadcrumbs } from "./breadcrumbs";
import { withCurrentPageCrumb } from "./crumbs";
import { PageBanners } from "./header/page-banners";
import { PageCardTopBar } from "./page-card-top-bar";
import { Crumb, PageBg } from "./types";

export type NewPageProps = PropsWithChildren<{
  /**
   * If true, the page banners (including `outsideTopbar`) are not displayed.
   * @default false
   */
  hideBanners?: boolean;
  /** Browser-tab (Helmet) title — never rendered on the page. */
  title?: string;
  /** Optional sidebar to display in the page */
  sidebar?: ReactNode;
  /** If true, the page will be constrained to the height of the screen (no body scroll) */
  contain?: boolean;
  /** Optional additional content to display above the page content and below the page banners */
  outsideTopbar?: ReactNode;
  /** If true, renders children directly without the Paper wrapper (full-bleed layout) */
  flush?: boolean;
  /** Breadcrumbs rendered in a `PageCardTopBar` at the top of the page card.
   *  The current page (`title`) is appended when the trail ends in a link.
   *  Not rendered in `flush` mode (there is no card). */
  crumbs?: Crumb[];
}>;

export const NewPage: FC<NewPageProps> = ({
  children,
  title,
  sidebar,
  outsideTopbar,
  crumbs,
  contain = false,
  hideBanners = false,
  flush = false,
}) => {
  const metadataContent = (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );

  const bannerContent = !hideBanners && (
    <PageBanners>{outsideTopbar}</PageBanners>
  );

  const crumbsBar = crumbs?.length ? (
    <PageCardTopBar>
      <Breadcrumbs showIcon crumbs={withCurrentPageCrumb(crumbs, title)} />
    </PageCardTopBar>
  ) : null;

  const pageContent = flush ? (
    <Column
      flex={1}
      minHeight={0}
      overflow={contain ? "hidden" : undefined}
      overscrollBehavior={contain ? "contain" : undefined}
    >
      {children}
    </Column>
  ) : (
    <Paper
      overflow={contain ? "hidden" : undefined}
      overscrollBehavior={contain ? "contain" : undefined}
      // If we have a sidebar, the content will be inside a row.
      // Setting the flex to 1 will make the content take up the remaining space.
      flex={sidebar ? 1 : undefined}
      minHeight={contain || sidebar ? 0 : undefined}
    >
      {crumbsBar}
      {children}
    </Paper>
  );

  if (sidebar) {
    return (
      <Box display="grid" gridTemplateRows="auto 1fr">
        {metadataContent}
        {bannerContent}
        <Row flex={1} minWidth={0}>
          {pageContent}
          {sidebar}
        </Row>
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateRows="auto 1fr"
      h="100%"
      maxH={contain ? "100vh" : "100%"}
      overflow={contain ? "hidden" : undefined}
    >
      {metadataContent}
      {bannerContent}
      {pageContent}
    </Box>
  );
};

type PageCardHeaderProps = {
  flush?: boolean;
  removeBoxShadow?: boolean;
  hasBorder?: boolean;
} & BoxProps;

export const PageCardHeader: FC<PropsWithChildren<PageCardHeaderProps>> = ({
  children,
  flush,
  removeBoxShadow = false,
  hasBorder = false,
  ...props
}) => {
  const borderProps = hasBorder
    ? ({
        borderBottomWidth: "hairline",
        borderBottomStyle: "solid",
        borderColor: "base.divider",
      } as const)
    : {};

  return (
    <Row
      px={flush ? 0 : 6}
      py={flush ? 0 : 4}
      gap={4}
      borderTopRadius="2xl"
      zIndex={1}
      boxShadow={
        removeBoxShadow
          ? undefined
          : "0 3px 6px -2px rgba(0, 0, 0, 0.02), 0 1px 1px 0px rgba(0, 0, 0, 0.02), 0 4px 20px 0px #FFF"
      }
      overflow="hidden"
      {...borderProps}
      {...props}
    >
      {children}
    </Row>
  );
};

type PageCardContentProps = {
  hasGradient?: boolean;
  flush?: boolean;
  variant?: PageBg;
} & Omit<BoxProps, "bg">;

const bgGradient = "linear(180deg, white, gray.50)";

export const PageCardContent: FC<PropsWithChildren<PageCardContentProps>> = ({
  hasGradient = false,
  children,
  flush,
  variant = "light",
  ...props
}) => {
  return (
    <Column
      flex={1}
      minHeight={0}
      p={flush ? 0 : 6}
      bg={variant === "dark" ? "base.lightBackground" : "white"}
      bgGradient={hasGradient ? bgGradient : undefined}
      {...props}
    >
      {children}
    </Column>
  );
};
